import { useState, useEffect, useCallback, useRef } from 'react';

/**
 * Generic hook for fetching report data with loading/error state.
 *
 * @param {Function} fetchFn   - Service function (dateRange, filters) => Promise
 * @param {Object}   dateRange - { startDate, endDate, filter }
 * @param {Object}   filters   - Optional extra filters (team_filter, agent_filter, etc.)
 * @param {boolean}  enabled   - If false, skip fetching (e.g. tab not active)
 */
export default function useReportData(fetchFn, dateRange, filters = {}, enabled = true) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const abortRef = useRef(null);

  const fetchData = useCallback(async () => {
    if (!enabled || !dateRange?.startDate || !dateRange?.endDate) {
      setLoading(false);
      return;
    }

    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setLoading(true);
    setError(null);

    try {
      const response = await fetchFn(dateRange, filters);
      if (!controller.signal.aborted) {
        setData(response.data);
      }
    } catch (err) {
      if (!controller.signal.aborted) {
        setError(err?.response?.data?.error || 'Failed to load report data');
      }
    } finally {
      if (!controller.signal.aborted) {
        setLoading(false);
      }
    }
  }, [fetchFn, dateRange?.startDate, dateRange?.endDate, JSON.stringify(filters), enabled]);

  useEffect(() => {
    fetchData();
    return () => {
      if (abortRef.current) abortRef.current.abort();
    };
  }, [fetchData]);

  return { data, loading, error, refetch: fetchData };
}
